import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useTenantBranding } from "@hooks/shared/use-tenant-branding";

/**
 * Renders the tenant's logo (the resolved, fetchable `mediaUrl` from the
 * tenant context) with a graceful fallback: when the tenant has no logo, or the
 * image fails to load, the `fallback` node is shown instead (e.g. the Oxygen
 * wordmark or the default app logo). This is the single place the client reads
 * the tenant logo, so both the login screen and the top nav stay consistent.
 */
export const TenantLogo = ({ height, maxWidth, fallback, alt }) => {
  const { logoUrl, name } = useTenantBranding();
  const [errored, setErrored] = useState(false);

  // Reset the error flag if the logo URL changes (e.g. context loads late).
  useEffect(() => setErrored(false), [logoUrl]);

  if (!logoUrl || errored) return fallback;

  return (
    <Box
      component="img"
      src={logoUrl}
      alt={alt || name || "Logo"}
      onError={() => setErrored(true)}
      sx={{ height, maxWidth, objectFit: "contain", display: "block" }}
    />
  );
};

TenantLogo.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fallback: PropTypes.node.isRequired,
  alt: PropTypes.string,
};
